import { ISubscription, ISubscriptionPlan, IUser } from "../types/interfaces";
import { message } from "antd";
import React, { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMe } from "../services/users";
import useLoading from "./useLoading";
import api from "../services/api";

/**
 * signIn is a local bypass mapping exactly to Go API without using Amplify.
 */
const signIn = async ({ username, password }: any) => { 
  try {
    const response = await api.post("/auth/login", { email: username, password });
    return { tokens: { accessToken: response.data.access_token, refreshToken: response.data.refresh_token } };
  } catch (error) {
    throw new Error("Invalid credentials");
  }
};

/**
 * signOut cleans the local state explicitly.
 */
const signOut = async () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
};

interface UseAuth {
  user: any;
  userData: any;
  initAuth: () => Promise<void>;
  isAuthenticated: boolean;
  userLoading: boolean;
  handleLogin: (email: string, password: string) => Promise<void>;
  handleLogout: () => Promise<void>;
  getUserData: () => Promise<string[]>;
  refreshUserData: () => Promise<void>;
  rolePermissions: string[] | undefined;
  subscription: ISubscription;
  subscriptionPlan: ISubscriptionPlan;
}

const authContext = createContext({} as UseAuth);

type Props = {
  children?: React.ReactNode;
};

/**
 * AuthProvider wraps the application exposing contextual RBAC properties natively.
 * It removes AWS Cognito instances providing local equivalents matching Go Ent.
 */
export const AuthProvider: React.FC<Props> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

const useAuth = () => React.useContext(authContext);

const useProvideAuth = (): UseAuth => {
  const navigate = useNavigate();
  const userLoading = useLoading(true);
  const [user, setUser] = useState<any>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState<IUser | null>();
  const [rolePermissions, setRolePermissions] = useState<string[] | undefined>(
    [],
  );
  const [subscription, setSubscription] = useState<any>();
  const [subscriptionPlan, setSubscriptionPlan] = useState<any>();

  useEffect(() => {
    initAuth();
  }, []);

  const initAuth = async () => {
    try {
      console.log("init auth");
      userLoading.start();
      const token = localStorage.getItem("accessToken");
      if (token) {
        await getUserData();
      } else {
        throw new Error("No token found");
      }
      setIsAuthenticated(true);
      userLoading.stop();
    } catch (err: any) {
      console.log("error init auth", err);
      await handleLogout();
      userLoading.stop();
    }
  };

  useEffect(() => {
    const subscription = userData?.subscription;
    const subscriptionPlan = userData?.plan;
    setSubscription(subscription);
    setSubscriptionPlan(subscriptionPlan);
  }, [userData]);

  const getUserData = async (): Promise<string[]> => {
    try {
      userLoading.start();
      const currentUserData = await getMe();
      const rolePermissions = currentUserData?.role?.permissions || [];
      const subPermissions = currentUserData?.plan?.permissions || [];
      // Use permissions from the backend response
      const userPermissionsFromBackend = currentUserData?.permissions || [];
      let combinePermissions: string[] = [...userPermissionsFromBackend];
      console.log("current", currentUserData);
      if (currentUserData.type === "user" || currentUserData.type === "empleado") {
        let userPermissions = currentUserData?.permissions || [];
        combinePermissions = combinePermissions?.concat(userPermissions);
      } else {
        if (subPermissions.length > 0) {
          combinePermissions = combinePermissions?.concat(subPermissions);
        }
      }
      if (rolePermissions.length > 0) {
        combinePermissions = combinePermissions?.concat(rolePermissions);
      }
      // Deduplicate
      combinePermissions = combinePermissions.filter((v, i, a) => a.indexOf(v) === i);
      setRolePermissions(combinePermissions);
      setUser({ username: currentUserData.email });
      setUserData(currentUserData);
      setIsAuthenticated(true);
      return combinePermissions;
    } catch (error) {
      console.log("error", error);
      return [];
    } finally {
      userLoading.stop();
    }
  };

  const refreshUserData = async () => {
    try {
      await getUserData();
    } catch (error) {
      console.log("error", error);
    }
  };

  // storeToken removed

  const handleLogin = async (email: string, password: string) => {
    try {
      const { tokens } = await signIn({ username: email, password }) as any;
      if (tokens?.accessToken) {
        localStorage.setItem("accessToken", tokens.accessToken);
      }
      if (tokens?.refreshToken) {
        localStorage.setItem("refreshToken", tokens.refreshToken);
      }
      const perms = await getUserData();

      // Smart redirect: find the first route the user has access to
      let targetRoute = "/admin/profile"; // fallback
      if (perms?.includes("ui:users:view")) {
        targetRoute = "/admin/users";
      } else if (perms?.includes("ui:map:point")) {
        targetRoute = "/admin/map/1";
      } else if (perms?.includes("ui:dashboard:view")) {
        targetRoute = "/admin/dashboard";
      }
      navigate(targetRoute);
    } catch (error) {
      console.log("error", error);
      message.error("Credenciales incorrectas, por favor intenta de nuevo");
      await handleLogout();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      handleSignOut(false);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const removeToken = () => {
    localStorage.removeItem("accessToken");
  };

  const handleSignOut = (shouldNavigate = true) => {
    setIsAuthenticated(false);
    setUser(null);
    setUserData(null);
    removeToken();
    signOut();
    if (shouldNavigate) navigate("/login");
  };

  return {
    getUserData,
    handleLogin,
    handleLogout,
    initAuth,
    isAuthenticated,
    user,
    userData,
    userLoading: userLoading.loading,
    refreshUserData,
    rolePermissions,
    subscription,
    subscriptionPlan,
  };
};

export default useAuth;
